import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { getCrimeDisplayName } from '../../constants/crimes';
import { getStateAbbreviation } from '../../constants/locations';
import { formatNumber, formatDifference, formatRank, formatPercentile } from '../../utils/formatting';
import { getValueColor } from '../../utils/coloring';
import { useChartState } from '../../hooks/charts';
import './Charts.css';

const TotalsChart = ({ 
  selectedLocation, 
  selectedCrime, 
  selectedDateRange, 
  onChartOptionChange, 
  selectedChartOption, 
  selectedSource,
  compareMode,
  onCompareModeChange
}) => {
  const [selectedChart, setSelectedChart] = useState(selectedChartOption);
  const {
    isMobile,
    isMenuOpen,
    setIsMenuOpen,
    isTouching,
    setIsTouching,
    menuRef,
    getDropdownLeftPosition
  } = useChartState();

  const current = selectedLocation?.data?.current;
  const locationType = selectedLocation.info?.type?.charAt(0).toUpperCase() + selectedLocation.info?.type?.slice(1);
  const isNation = selectedLocation.info?.type?.toLowerCase() === 'nation';
  const isCounty = selectedLocation.info?.type?.toLowerCase() === 'county';
  const isCity = selectedLocation.info?.type?.toLowerCase() === 'city';
  const isCountyOrCity = isCounty || isCity;
  
  const stateAbbr = React.useMemo(() => {
    if (isCountyOrCity && selectedLocation?.info?.name?.includes(',')) {
      const stateName = selectedLocation.info.name.split(',')[1]?.trim();
      return getStateAbbreviation(stateName);
    }
    return getStateAbbreviation(selectedLocation);
  }, [isCountyOrCity, selectedLocation]);

  useEffect(() => {
    if (selectedChartOption) {
      if (isCountyOrCity) {
        const prefix = compareMode === 'state' ? stateAbbr : 'US';
        
        if (selectedChartOption.match(/^(US|[A-Z]{2})\s+/) && 
            !selectedChartOption.startsWith(prefix)) {
          const newOption = selectedChartOption.replace(/^(US|[A-Z]{2})\s+/, `${prefix} `);
          setSelectedChart(newOption);
        } else {
          setSelectedChart(selectedChartOption);
        }
      } else {
        setSelectedChart(selectedChartOption);
      }
    }
  }, [selectedChartOption, compareMode, isCountyOrCity, stateAbbr]);

  if (selectedLocation?.loading || !selectedLocation?.data?.current || selectedLocation?.info?.name?.startsWith('All ')) {
    return null;
  }

  const hasCrimeData = [
    'all_crime_count', 'violent_crime_count', 'property_crime_count',
    'murder_count', 'rape_count', 'robbery_count', 'aggravated_assault_count',
    'burglary_count', 'larceny_theft_count', 'motor_vehicle_theft_count'
  ].some(field => 
    current[field] !== null && 
    current[field] !== undefined && 
    current[field] !== 'N/A'
  );
  
  if (!hasCrimeData) {
    return <div className="loading">No Data Available</div>;
  }

  const handleTouchStart = () => {
    setIsTouching(true);
  };
  
  const handleTouchEnd = () => {
    setIsTouching(false);
  };
  
  const chartOptions = [
    'Total Crime',
    `${isCountyOrCity ? `${compareMode === 'state' ? stateAbbr : 'US'} ` : ''}${locationType} Average`,
    `${isCountyOrCity ? `${compareMode === 'state' ? stateAbbr : 'US'} ` : ''}${locationType} Avg Difference`,
    `% ${isCountyOrCity ? `${compareMode === 'state' ? stateAbbr : 'US'} ` : ''}${locationType} Avg Difference`,
    `Total Crime vs ${isCountyOrCity ? `${compareMode === 'state' ? stateAbbr : 'US'} ` : ''}${locationType} Average`,
    `${isCountyOrCity ? `${compareMode === 'state' ? stateAbbr : 'US'} ` : ''}${locationType} Rank`,
    `${isCountyOrCity ? `${compareMode === 'state' ? stateAbbr : 'US'} ` : ''}${locationType} Percentile`
  ];

  if (!selectedChart || !chartOptions.includes(selectedChart)) {
    return null;
  }

  const getChartOption = (baseOption) => {
    if (baseOption === 'Total Crime') {
      return baseOption;
    }
    
    if (isCountyOrCity) {
      const prefix = compareMode === 'state' ? stateAbbr : 'US';
      return baseOption.replace(`${locationType}`, `${prefix} ${locationType}`);
    }
    
    return baseOption;
  };

  const getTotalLocations = () => {
    if (isCountyOrCity) {
      return compareMode === 'state' ? 
        (isCounty ? current.total_counties_state : current.total_cities_state) || current.total_locations : 
        (isCounty ? current.total_counties_national : current.total_cities_national) || current.total_locations;
    }
    
    return current.total_locations || 0;
  };  

  const extractNumber = (rank) => {
    if (typeof rank !== 'string') return rank;
    const match = rank.match(/\d+/);
    return match ? parseInt(match[0], 10) : rank;
  };

  const getComparisonFields = (prefix) => {
    const rankField = isCountyOrCity
      ? compareMode === 'state'
        ? `${prefix}_state_count_rank`
        : `${prefix}_national_count_rank`
      : `${prefix}_count_rank`;
    
    const percentileField = isCountyOrCity
      ? compareMode === 'state'
        ? `${prefix}_state_count_percentile`
        : `${prefix}_national_count_percentile`
      : `${prefix}_count_percentile`;
    
    return {
      nationalAvg: isCountyOrCity && compareMode === 'state' 
        ? current[`${prefix}_state_count_avg`]
        : current[`${prefix}_national_count_avg`],
      nationalDiff: isCountyOrCity && compareMode === 'state'
        ? current[`${prefix}_state_count_avg_diff`]
        : current[`${prefix}_national_count_avg_diff`],
      nationalDiffPct: isCountyOrCity && compareMode === 'state'
        ? current[`${prefix}_state_count_avg_diff_pct`]
        : current[`${prefix}_national_count_avg_diff_pct`],
      rank: current[rankField],
      rankValue: extractNumber(current[rankField]),
      percentile: (current[percentileField] > 0 && current[percentileField] < 1) ? 1 : current[percentileField]
    };
  };

  const transformData = () => {
    if (selectedSource === 'BJS_NCVS') {
      return [
        {
          name: 'All Crime',
          currentTotal: current.all_crime_count,
        },
        {
          name: 'Violent Crime',
          currentTotal: current.violent_crime_count,
        },
        {
          name: 'Rape',
          currentTotal: current.rape_count,
        },
        {
          name: 'Robbery',
          currentTotal: current.robbery_count,
        },
        {
          name: 'Aggravated Assault',
          currentTotal: current.aggravated_assault_count,
        },
        {
          name: 'Simple Assault',
          currentTotal: current.simple_assault_count,
        },
        {
          name: 'Property Crime',
          currentTotal: current.property_crime_count,
        },
        {
          name: 'Burglary',
          currentTotal: current.burglary_count,
        },
        {
          name: 'Trespassing',
          currentTotal: current.trespassing_count,
        },
        {
          name: 'Larceny/Theft',
          currentTotal: current.larceny_theft_count,
        },
        {
          name: 'Motor Vehicle Theft',
          currentTotal: current.motor_vehicle_theft_count,
        }
      ];
    }
  
    return [
      {
        name: 'All Crime',
        currentTotal: current.all_crime_count,
        ...getComparisonFields('all_crime')
      },
      {
        name: 'Violent Crime',
        currentTotal: current.violent_crime_count,
        ...getComparisonFields('violent_crime')
      },
      {
        name: 'Murder',
        currentTotal: current.murder_count,
        ...getComparisonFields('murder')
      },
      {
        name: 'Rape',
        currentTotal: current.rape_count,
        ...getComparisonFields('rape')
      },
      {
        name: 'Robbery',
        currentTotal: current.robbery_count,
        ...getComparisonFields('robbery')
      },
      {
        name: 'Aggravated Assault',
        currentTotal: current.aggravated_assault_count,
        ...getComparisonFields('aggravated_assault')
      },
      {
        name: 'Property Crime',
        currentTotal: current.property_crime_count,
        ...getComparisonFields('property_crime')
      },
      {
        name: 'Burglary',
        currentTotal: current.burglary_count,
        ...getComparisonFields('burglary')
      },
      {
        name: 'Larceny/Theft',
        currentTotal: current.larceny_theft_count,
        ...getComparisonFields('larceny_theft')
      },
      {
        name: 'Motor Vehicle Theft',
        currentTotal: current.motor_vehicle_theft_count,
        ...getComparisonFields('motor_vehicle_theft')
      }
    ];
  };

  const chartData = transformData();
  
  const getMaxValue = () => {
    switch (selectedChart) {
      case getChartOption('Total Crime'):
        return Math.max(...chartData.map(item => item.currentTotal || 0));
      case getChartOption(`${locationType} Average`):
        return Math.max(...chartData.map(item => item.nationalAvg || 0));
      case getChartOption(`${locationType} Avg Difference`):
      case getChartOption(`% ${locationType} Avg Difference`):
        const values = chartData.map(item => 
          selectedChart === getChartOption(`${locationType} Avg Difference`) ? 
            item.nationalDiff : item.nationalDiffPct
        );
        return Math.max(Math.abs(Math.min(...values)), Math.max(...values));
      case getChartOption(`${locationType} Rank`):
        return getTotalLocations();
      case getChartOption(`${locationType} Percentile`):
        return 100;
      default:
        return Math.max(
          ...chartData.map(item => Math.max(item.currentTotal || 0, item.nationalAvg || 0))
        );
    }
  };

  const maxValue = getMaxValue();
  
  const getAxisMaxValue = (value) => {
    if (selectedChart === getChartOption(`${locationType} Rank`)) {
      return getTotalLocations();
    } else if (selectedChart === getChartOption(`${locationType} Percentile`)) {
      return 100;
    } else if (selectedChart.includes('%')) {
      return Math.ceil(value / 10) * 10;
    } else {
      const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
      const normalized = value / magnitude;
      const rounded = Math.ceil(normalized / 0.1) * 0.1;
      return rounded * magnitude;
    }
  };
  
  const getTicks = (maxVal) => {
    if (selectedChart === getChartOption(`${locationType} Rank`)) {
      return [1, Math.ceil(getTotalLocations()/4), Math.ceil(getTotalLocations()/2), 
        Math.ceil(getTotalLocations()*3/4), getTotalLocations()];
    } else if (selectedChart === getChartOption(`${locationType} Percentile`)) {
      return [1, 25, 50, 75, 100];
    } else if (selectedChart.includes('Difference')) {
      if (isMobile && window.innerWidth < 400) {
        return [-maxVal, 0, maxVal];
      }
      return [-maxVal, -maxVal/2, 0, maxVal/2, maxVal];
    } else {
      if (isMobile && window.innerWidth < 400) {
        return [0, maxVal/2, maxVal];
      }
      return [0, maxVal/4, maxVal/2, maxVal*3/4, maxVal];
    }
  };

  const renderTotalBar = (props) => {
    const { x, y, width, height, payload, dataKey } = props;
    const isMainCategory = ['All Crime', 'Violent Crime', 'Property Crime'].includes(payload.name);
    const isRank = selectedChart === getChartOption(`${locationType} Rank`);
    const isPercentile = selectedChart === getChartOption(`${locationType} Percentile`);
    const value = props.dataKey === 'currentTotal' ? payload.currentTotal : payload.nationalAvg;
        
    const barWidth = Math.max(1, width || 0);
    if (!barWidth) return null;

    if (!isRank && !isPercentile) {
      if (value === null || value === undefined || isNaN(value)) {
        return (
          <g>
            <text
              x={x + 5}
              y={y + height / 2}
              dy="0.35em"
              textAnchor="start"
              fill="var(--color)"
              style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}
            >
              -
            </text>
          </g>
        );
      }

      if (value === 0) {
        return (
          <g>
            <text
              x={x + 5}
              y={y + height / 2}
              dy="0.35em"
              textAnchor="start"
              fill="var(--color)"
              style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}
            >
              0
            </text>
          </g>
        );
      }
    }
    
    if (isRank || isPercentile) {
      const value = isRank ? payload.rankValue : payload.percentile;     
      const maxValue = isRank ? getTotalLocations() : 100;
      const isNearMax = value >= (maxValue * 0.9);
      const hasValidValue = value !== null && value !== undefined && !isNaN(value);
      
      return (
        <g>
          <rect
            x={x}
            y={y}
            width={barWidth}
            height={height}
            fill="transparent"
          />
          {hasValidValue && (
            <circle
              cx={x + barWidth}
              cy={y + height/2}
              r={6}
              fill={`var(--navy)`}
              fillOpacity={isMainCategory ? 0.6 : 0.3}
              stroke="black"
            />
          )}
          <text
            x={hasValidValue ? x + barWidth + (isNearMax ? -12 : 12) : x + 5}
            y={y + height/2}
            dy="0.35em"
            textAnchor={isNearMax ? "end" : "start"}
            style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}
          >
            {isRank ? formatRank(payload.rank) : formatPercentile(payload.percentile)}
          </text>
        </g>
      );
    }
    
    const maxAxisValue = getAxisMaxValue(maxValue);
    const relativePercentage = (value / maxAxisValue);
    const isComparison = selectedChart === getChartOption(`Total Crime vs ${locationType} Average`);
    const isTextInside = isComparison ? relativePercentage > (isMobile ? 0.5 : 0.25) : relativePercentage > (isMobile ? 0.25 : 0.12);
    const diff = isComparison && dataKey === 'currentTotal' ? payload.currentTotal - payload.nationalAvg : null;
    const useComparisonStyle = isComparison && dataKey === 'currentTotal';
    const isNationalAvgNull = payload.nationalAvg === null || payload.nationalAvg === undefined || isNaN(payload.nationalAvg);
    const color = (isNationalAvgNull && isComparison)  ? 'silver' : (useComparisonStyle ? getValueColor(diff) : 'navy');
    const chartAreaStart = isMobile ? 160 : 180;
    
    const textX = isTextInside ? 
      Math.max(chartAreaStart + 50, x + barWidth - 4) : 
      Math.max(chartAreaStart + 4, x + barWidth + 4);  
    
    return (
      <g>
        <rect
          x={x}
          y={y}
          width={barWidth}
          height={height}
          fill={`var(--${color})`}
          fillOpacity={isMainCategory ? 0.6 : 0.3}
          stroke={`var(--${color})`}
          strokeWidth={1}
        />
        <text
          x={textX}
          y={y + height / 2}
          dy="0.35em"
          textAnchor={isTextInside ? "end" : "start"}
          fill={isMainCategory && isTextInside ? 
            ((isNationalAvgNull && isComparison) ? 'black' : 
             useComparisonStyle ? `var(--very-light-${color})` : 'var(--light-gray)') 
            : ((isNationalAvgNull && isComparison) ? 'black' : `var(--${color})`)}
          style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}
        >
          {formatNumber(value)}
          {isComparison && dataKey === 'currentTotal' && (
            <tspan 
              dx={4}
              style={{ fontWeight: 400 }}
            >
              ({formatDifference(payload.nationalDiff)})
            </tspan>
          )}
        </text>
      </g>
    );
  };

  const renderDifferenceBar = (props) => {
    const { x, y, width, height, payload } = props;
    const isMainCategory = ['All Crime', 'Violent Crime', 'Property Crime'].includes(payload.name);
    const value = selectedChart === getChartOption(`${locationType} Avg Difference`) ? payload.nationalDiff : payload.nationalDiffPct;

    const isZero = parseFloat(value) === 0;
    
    if (isZero) {
      return (
        <g>
          <text
            x={x + 10}
            y={y + height / 2}
            dy="0.35em"
            textAnchor="start"
            fill="black"
            style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}
          >
            0%
          </text>
        </g>
      );
    }

    if (value === null || value === undefined || isNaN(value)) {
      return (
        <g>
          <text
            x={x + 5}
            y={y + height / 2}
            dy="0.35em"
            textAnchor="start"
            fill="var(--color)"
            style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}
          >
            -
          </text>
        </g>
      );
    }

    const isNegative = value < 0;
    const color = getValueColor(value);
    
    const barWidth = Math.abs(width || 0);
    if (!barWidth) return null;    
    const barX = isNegative ? x + width : x;
    
    const maxAxisValue = getAxisMaxValue(maxValue);
    const relativePercentage = Math.abs(value / maxAxisValue);
    const isTextInside = relativePercentage > (isMobile ? 0.5 : 0.2);
    
    const textX = isNegative ? 
      (isTextInside ? barX + 4 : barX - 4) : 
      (isTextInside ? x + barWidth - 4 : x + barWidth + 4);
    
    return (
      <g>
        <rect
          x={barX}
          y={y}
          width={barWidth}
          height={height}
          fill={`var(--${color})`}
          fillOpacity={isMainCategory ? 0.6 : 0.3}
          stroke={`var(--${color})`}
          strokeWidth={1}
        />
        <text
          x={textX}
          y={y + height / 2}
          dy="0.35em"
          textAnchor={isNegative ? 
            (isTextInside ? "start" : "end") : 
            (isTextInside ? "end" : "start")}
          fill={isMainCategory && isTextInside ? 
            `var(--very-light-${color})` : 
            `var(--${color})`}
          style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}
        >
          {formatDifference(value, selectedChart === getChartOption(`% ${locationType} Avg Difference`))}
        </text>
      </g>
    );
  };

  const renderCustomLegend = () => {
    const renderLegendBar = () => {
      switch (selectedChart) {
        case getChartOption(`${locationType} Avg Difference`):
        case getChartOption(`% ${locationType} Avg Difference`):
        case getChartOption(`Total Crime vs ${locationType} Average`):
          return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ 
                width: isMobile ? '30px' : '40px', 
                height: '12px',
                background: 'linear-gradient(to right, rgba(0, 128, 0, 0.6), rgba(0, 128, 0, 0.3))',
                borderLeft: '1px solid var(--green)',
                borderTop: '1px solid var(--green)',
                borderBottom: '1px solid var(--green)'
              }} />
              <div style={{ 
                width: isMobile ? '30px' : '40px', 
                height: '12px',
                background: 'linear-gradient(to right, rgba(255, 0, 0, 0.3), rgba(255, 0, 0, 0.6))',
                borderRight: '1px solid var(--red)',
                borderTop: '1px solid var(--red)',
                borderBottom: '1px solid var(--red)'
              }} />
            </div>
          );

        case getChartOption(`${locationType} Rank`):
        case getChartOption(`${locationType} Percentile`):
          return (
            <div style={{ 
              width: '12px', 
              height: '12px',
              background: 'linear-gradient(to right, rgba(26, 44, 78, 0.3), rgba(26, 44, 78, 0.6))',
              border: '1px solid var(--navy)',
              borderRadius: '50%',
            }} />
          );

        default:
          return (
            <div style={{ 
              width: isMobile ? '60px' : '80px', 
              height: '12px',
              background: 'linear-gradient(to right, rgba(26, 44, 78, 0.3), rgba(26, 44, 78, 0.6))',
              border: '1px solid var(--navy)'
            }} />
          );
      }
    };

    const getDisplayTitle = () => {
      if (selectedChart === getChartOption(`Total Crime vs ${locationType} Average`)) {
        return 'Total Crime';
      }
      if (selectedSource === 'BJS_NCVS' && selectedChart === 'Total Crime') {
        return 'Total Victimizations';
      }
      return selectedChart;
    };

    return (
      <div style={{ 
        display: 'flex', 
        gap: '20px', 
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 0 10px',
        position: 'relative'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {renderLegendBar()}
          <div 
            ref={menuRef}
            style={{ 
              display: 'flex', 
              alignItems: 'center',
              cursor: isNation ? 'default' : 'pointer',
              gap: '4px',
              position: 'relative'
            }}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}>{getDisplayTitle()}</span>
            {!isNation && (
              <>
                <ChevronDown className="chart-legend-chevron" style={{ width: '16px', height: '16px' }} />
              
                {isMenuOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: getDropdownLeftPosition(selectedChart),
                    backgroundColor: 'white',
                    border: '1px solid var(--silver)',
                    borderRadius: '4px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    zIndex: 1000,
                    minWidth: 'max-content',
                    marginTop: '4px',
                    whiteSpace: 'nowrap'
                  }}>
                    {chartOptions.map((option, index) => (
                      <div
                        key={option}
                        style={{
                          padding: '8px 12px',
                          cursor: selectedChart === option ? 'not-allowed' : 'pointer',
                          fontSize: isMobile ? '11px' : '12px',
                          fontWeight: 600,
                          textAlign: 'left',
                          backgroundColor: selectedChart === option ? 'var(--light-gray)' : 'transparent',
                          borderBottom: index < chartOptions.length - 1 ? '1px solid var(--border-color)' : 'none',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          position: 'relative', 
                          boxSizing: 'border-box',
                          height: '31px'
                        }}
                        onMouseEnter={(e) => {
                          if (selectedChart !== option) {
                            e.currentTarget.style.backgroundColor = 'var(--silver)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedChart !== option) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                        onClick={(e) => {
                          if (selectedChart === option) {
                            e.stopPropagation();
                            return;
                          }
                          e.stopPropagation();
                          setSelectedChart(option);
                          onChartOptionChange(option);
                          setIsMenuOpen(false);
                        }}
                      >
                        <span style={{ opacity: selectedChart === option ? 0.5 : 1 }}>
                          {option}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {selectedChart === getChartOption(`Total Crime vs ${locationType} Average`) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ 
              width: isMobile ? '60px' : '80px', 
              height: '12px',
              background: 'linear-gradient(to right, rgba(26, 44, 78, 0.3), rgba(26, 44, 78, 0.6))',
              border: '1px solid var(--navy)'
            }} />
            <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}>
              {isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType} Average
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`chart-container ${isMenuOpen ? 'active' : ''}`}>
      {renderCustomLegend()}
      <div 
        className="chart-content" 
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        <ResponsiveContainer>
          <BarChart
            key={selectedChart}
            data={chartData}
            layout="vertical"
            margin={{ top: 0, right: isMobile ? 30 : 40, left: 20, bottom: 0 }}
          >
            <XAxis 
              type="number"
              tickFormatter={(value) => {
                if (selectedChart === getChartOption(`${locationType} Percentile`)) {
                  return formatPercentile(value);
                } else if (selectedChart === getChartOption(`${locationType} Rank`)) {
                  return formatRank(value);
                } else if (selectedChart.includes('Difference')) {
                  return value === 0 ? '0' : formatDifference(value, selectedChart.includes('%'));
                } else {
                  return value === 0 ? '0' : formatNumber(value);
                }
              }}
              domain={
                selectedChart === getChartOption(`${locationType} Rank`) ? 
                  [1, getTotalLocations()] :
                selectedChart === getChartOption(`${locationType} Percentile`) ?
                  [1, 100] :
                selectedChart.includes('Difference') ? 
                  [-getAxisMaxValue(maxValue), getAxisMaxValue(maxValue)] : 
                  [0, getAxisMaxValue(maxValue)]
              }
              axisLine={{ stroke: 'var(--border-color)' }}
              tickLine={{ stroke: 'var(--border-color)' }}
              ticks={getTicks(getAxisMaxValue(maxValue))}
              tick={{ fontSize: isMobile ? 12 : 14, fontWeight: 400, fill: 'black' }}
              interval="preserveStartEnd"
              allowDecimals={false}
              height={isMobile ? 20 : 30}
            />
            <YAxis 
              type="category" 
              dataKey="name"
              width={isMobile ? 140 : 160}
              axisLine={{ stroke: 'var(--border-color)' }}
              tickLine={false} 
              interval={0}
              tick={(props) => {
                const { y, payload, index, height, visibleTicksCount } = props;
                let indent = 12;
                if (['Violent Crime', 'Property Crime'].includes(payload.value)) {
                  indent = 24;
                } else if (['Murder', 'Rape', 'Robbery', 'Aggravated Assault', 'Burglary', 'Larceny/Theft', 'Motor Vehicle Theft', 'Simple Assault', 'Trespassing'].includes(payload.value)) {
                  indent = 36;
                }

                const spacing = height / visibleTicksCount;
                const isSelected = payload.value === getCrimeDisplayName(selectedCrime, selectedSource) || 
                                (selectedCrime === 'all_crime' && payload.value === 'All Crime');

                return (
                  <g>
                    {['All Crime', 'Violent Crime', 'Property Crime'].includes(payload.value) && (
                      <rect
                        x={0}
                        y={y - spacing/2}
                        width={isMobile ? "calc(100% - 30px)" : "calc(100% - 40px)"}
                        height={spacing}
                        fill="var(--light-gray)"
                        fillOpacity={1}
                      />
                    )}
                    {isSelected && (
                      <rect
                        x={0}
                        y={y - spacing/2}
                        width={isMobile ? "calc(100% - 30px)" : "calc(100% - 40px)"}
                        height={spacing}
                        fill="#fef9c3"
                        fillOpacity={0.9}
                      />
                    )}
                    {index === 0 && (
                      <>
                        <line 
                          x1={0} 
                          y1={y - spacing/2}
                          x2={isMobile ? 160 : 180} 
                          y2={y - spacing/2}
                          stroke="var(--border-color)" 
                          strokeWidth={4}
                        />
                        <line 
                          x1={0} 
                          y1={y + spacing/2}
                          x2={isMobile ? 160 : 180} 
                          y2={y + spacing/2}
                          stroke="var(--border-color)" 
                          strokeWidth={2}
                        />
                      </>
                    )}
                    {index > 0 && (
                      <line 
                        x1={0} 
                        y1={y + spacing/2} 
                        x2={isMobile ? 160 : 180} 
                        y2={y + spacing/2} 
                        stroke="var(--border-color)" 
                        strokeWidth={1}
                      />
                    )}
                    {index === chartData.length - 1 && (
                      <line 
                        x1={0} 
                        y1={y + spacing/2} 
                        x2={isMobile ? 160 : 180} 
                        y2={y + spacing/2} 
                        stroke="var(--border-color)" 
                        strokeWidth={1}
                      />
                    )}
                    <text 
                      x={indent} 
                      y={y} 
                      dy={4} 
                      textAnchor="start" 
                      style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 600 }}
                    >
                      {payload.value}
                    </text>
                  </g>
                );
              }}
            />
            <CartesianGrid 
              stroke="var(--border-color)"
              strokeDasharray="none"
              horizontal={true}
              horizontalCoordinatesGenerator={({ yAxis }) => {
                return yAxis.scale.domain().map(y => yAxis.scale(y));
              }}
            />
            <line 
              x1={isMobile ? 160 : 180} 
              y1={0} 
              x2="100%" 
              y2={0} 
              stroke="var(--border-color)" 
              strokeWidth={4}
              style={{ transform: `translateX(-${isMobile ? 30 : 40}px)` }}
            />
            <Tooltip 
              cursor={{
                fill: 'rgba(0, 0, 0, 0.1)',
                x: -160,
                width: "calc(100% + 160px)"
              }}
              active={isMobile ? isTouching : undefined}
              wrapperStyle={isMobile ? { transform: 'translateX(140px)' } : {}}
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  const color = getValueColor(data.nationalDiff);
                  
                  const getRowStyle = (type) => ({
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    gap: '24px',
                    backgroundColor: (
                      (!isNation && selectedChart === getChartOption('Total Crime') && type === 'Total Crime') ||
                      (selectedChart === getChartOption(`${locationType} Average`) && type === 'Average') ||
                      (selectedChart === getChartOption(`${locationType} Avg Difference`) && type === 'Avg Difference') ||
                      (selectedChart === getChartOption(`% ${locationType} Avg Difference`) && type === '% Avg Difference') ||
                      (selectedChart === getChartOption(`${locationType} Rank`) && type === 'Rank') ||
                      (selectedChart === getChartOption(`${locationType} Percentile`) && type === 'Percentile') ||
                      (selectedChart === getChartOption(`Total Crime vs ${locationType} Average`) && 
                        (type === 'Total Crime' || type === 'Average' || type === 'Avg Difference'))
                    ) ? '#fef9c3' : 'transparent',
                    padding: '2px 4px',
                    margin: '0 -4px'
                  });
                  
                  return (
                    <div className="custom-tooltip" style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      padding: '12px',
                      border: '1px solid var(--silver)',
                      borderRadius: '4px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      <div style={{ 
                        margin: '0 0 4px 0',
                        fontWeight: 600,
                        fontSize: isMobile ? '13px' : '14px',
                        color: 'black',
                        textAlign: 'left',
                        paddingBottom: '2px',
                      }}>
                        {isCountyOrCity ? ((selectedLocation.info.name.split(',')[0]) + ', ' + stateAbbr) : selectedLocation.info.name}
                      </div>
                      <div style={{ 
                        margin: '0 0 4px 0',
                        fontWeight: 400,
                        fontSize: isMobile ? '13px' : '14px',
                        textAlign: 'left',
                        borderBottom: '1px solid var(--silver)',
                        paddingBottom: '8px'
                      }}>
                        {selectedDateRange}
                      </div>
                      <div style={{ fontSize: isMobile ? '11px' : '12px', display: 'grid', gap: '4px', paddingTop: '6px' }}>
                        <div style={getRowStyle('Total Crime')}>
                          <span style={{ fontWeight: 600 }}>
                            Total {data.name === 'All Crime' 
                              ? (selectedSource === 'BJS_NCVS' ? 'Victimizations' : 'Crime') 
                              : data.name}:
                          </span>
                          <strong>{formatNumber(data.currentTotal)}</strong>
                        </div>
                        {!isNation && (
                          <>
                            <div style={getRowStyle('Average')}>
                              <span style={{ fontWeight: 600 }}>
                                {isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType} Average:
                              </span>
                              <span>{formatNumber(data.nationalAvg)}</span>
                            </div>
                            <div style={getRowStyle('Avg Difference')}>
                              <span style={{ fontWeight: 600 }}>
                                {isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType} Avg Difference:
                              </span>
                              <span style={{ color: `var(--${color})` }}>
                                {formatDifference(data.nationalDiff)}
                              </span>
                            </div>
                            <div style={getRowStyle('% Avg Difference')}>
                              <span style={{ fontWeight: 600 }}>
                                % {isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType} Avg Difference:
                              </span>
                              <span style={{ color: `var(--${color})` }}>
                                {formatDifference(data.nationalDiffPct, true)}
                              </span>
                            </div>
                            <div style={getRowStyle('Rank')}>
                              <span style={{ fontWeight: 600 }}>
                                {isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType} Rank:
                              </span>
                              <span>{formatRank(data.rank)}</span>
                            </div>
                            <div style={getRowStyle('Percentile')}>
                              <span style={{ fontWeight: 600 }}>
                                {isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType} Percentile:
                              </span>
                              <span>{formatPercentile(data.percentile)}</span>
                            </div>
                          </>
                        )}  
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            {selectedChart === getChartOption(`Total Crime vs ${locationType} Average`) ? (
              <>
                <Bar dataKey="currentTotal" shape={renderTotalBar} />
                <Bar dataKey="nationalAvg" shape={renderTotalBar} />
              </>
            ) : (
              <Bar
                dataKey={
                  selectedChart === getChartOption('Total Crime') ? 'currentTotal' :
                  selectedChart === getChartOption(`${locationType} Average`) ? 'nationalAvg' :
                  selectedChart === getChartOption(`${locationType} Rank`) ? 'rankValue' :
                  selectedChart === getChartOption(`${locationType} Percentile`) ? 'percentile' :
                  selectedChart === getChartOption(`${locationType} Avg Difference`) ? 'nationalDiff' : 'nationalDiffPct'
                }
                shape={selectedChart.includes('Difference') ? renderDifferenceBar : renderTotalBar}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TotalsChart;